import React, { useMemo, useState } from 'react';
import _ from 'underscore';

import FileIcon from './FileIcon';
import ConnectingLine from './ConnectingLine';
import BubbleText from './BubbleText';
import { Images } from 'assets/images';
import { courseExerciseStatus } from 'constants/tests';
import { colors } from 'global/colors';
import { useWindowSize } from 'hooks/useWindowSize';
import { getHeightOfContainer, getTopCalculation, getOrderOfLevels, getCharacterImages } from './styleFunctions';
import {
  FlowContainer,
  FlowHeadingContainer,
  IconAndNameContainer,
  FlowLevelName,
  InvisibleItem,
  LevelsContainer,
  LevelColumn,
  CertificateSVG,
  IconImage,
  EmptyDiv,
  PracticeButton,
} from './styles';

const LevelItem = ({
  item,
  course,
  language,
  isCompleted,
  languagesCompleted,
  syllabusIndex,
  moduleIndex,
  lesson,
  moduleName,
  levels,
  lastCompletedLevel,
  handleClickOnLevelName,
  isPaidExercise,
  checkHighlightedIndex,
  getStatusOfLevel,
  exerciseId,
  testImpl,
  inputExercise,
  isCurrentModule,
  isMobile,
  exerciseAccessLevel,
}) => {
  const { level, index: levelIndex } = item;

  let isLevelCompleted = isCompleted ? true : level.isCompleted;
  if (language === 'any' && !isLevelCompleted) {
    for (const lang in course.languages) {
      if (course.languages.hasOwnProperty(lang)) {
        isLevelCompleted = languagesCompleted[lang][levelIndex]?.isCompleted;
        if (isLevelCompleted) break;
      }
    }
  }

  lastCompletedLevel = isLevelCompleted;

  const isLastLevelOfCourse =
    syllabusIndex === course.syllabus.length - 1 &&
    moduleIndex === lesson.modules.length - 1 &&
    levelIndex === levels.length - 1;

  const shouldRenderConnectingLine = levelIndex !== 0 && !level.isExtraDiv;
  const shouldRenderLastConnectingLine = level.isExtraDiv && !isLastLevelOfCourse;

  const startElementId = shouldRenderConnectingLine
    ? `id-${syllabusIndex}-${moduleIndex}-${levelIndex - 1}`
    : `last-invisible-div-${syllabusIndex}-${moduleIndex}`;

  const endElementId = shouldRenderConnectingLine
    ? `id-${syllabusIndex}-${moduleIndex}-${levelIndex}`
    : `id-${syllabusIndex}-${moduleIndex}-${levels.length - 2}`;

  const status = shouldRenderConnectingLine
    ? getStatusOfLevel(isLevelCompleted, levelIndex)
    : getStatusOfLevel(lastCompletedLevel, levels.length - 2);

  return (
    <React.Fragment key={levelIndex}>
      <IconAndNameContainer
        isOddDiv={levelIndex % 2 !== 0}
        isLocked={isPaidExercise}
        top={getTopCalculation(levels.length, levelIndex)}
        onClick={(event) =>
          handleClickOnLevelName(event, exerciseId, levelIndex, testImpl, inputExercise, exerciseAccessLevel)
        }
      >
        {level.isExtraDiv ? (
          <InvisibleItem id={`last-invisible-div-${syllabusIndex}-${moduleIndex}`} />
        ) : (
          <>
            {level.levelName.toLowerCase().includes('certificate') ? (
              <CertificateSVG
                isMobile={isMobile}
                src={
                  courseExerciseStatus.COMPLETED === getStatusOfLevel(isLevelCompleted, levelIndex)
                    ? Images.certificateActive
                    : Images.certificateInactive
                }
              />
            ) : (
              <FileIcon
                id={`id-${syllabusIndex}-${moduleIndex}-${levelIndex}`}
                status={getStatusOfLevel(isLevelCompleted, levelIndex)}
                isMobile={isMobile}
              />
            )}
            <FlowLevelName isMobile={isMobile} isLevelUnlocked={isLevelCompleted || checkHighlightedIndex(levelIndex)}>
              {level.levelName}
            </FlowLevelName>
          </>
        )}
      </IconAndNameContainer>
      {(shouldRenderConnectingLine || shouldRenderLastConnectingLine) && (
        <ConnectingLine
          key={isCurrentModule}
          startElementId={startElementId}
          endElementId={endElementId}
          parentContainerId={`module-${syllabusIndex}-${moduleIndex}`}
          status={status}
          isMobile={isMobile}
          isLastLine={shouldRenderLastConnectingLine}
        />
      )}
    </React.Fragment>
  );
};

const courseIcons = require.context('assets/courseIcons');

export default function CourseProgress({
  user,
  exerciseId,
  syllabusIndex,
  levelNames,
  moduleName,
  exerciseName,
  isCompletedInAnyLanguage,
  languagesCompleted,
  testImpl,
  moduleIndex,
  inputExercise,
  icon,
  exerciseAccessLevel,
  lesson,
  firstIncompleteLevel,
  course,
  language,
  handleClickOnLevelName,
}) {
  const { width } = useWindowSize();
  const isMobile = width < 520;

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const isCurrentModule = useMemo(
    () => syllabusIndex === firstIncompleteLevel.syllabusIndex && moduleIndex === firstIncompleteLevel.moduleIndex,
    [firstIncompleteLevel, syllabusIndex, moduleIndex]
  );
  const isFirstLevel = useMemo(
    () =>
      firstIncompleteLevel.syllabusIndex === 0 &&
      firstIncompleteLevel.moduleIndex === 0 &&
      firstIncompleteLevel.levelIndex === 0,
    [firstIncompleteLevel]
  );

  let isPaidExercise = false;
  let isCompleted = isCompletedInAnyLanguage
    ? true
    : _.every(_.pluck(levelNames, 'isCompleted'), (levelCompleted) => levelCompleted === true);
  let lastCompletedLevel = false;

  const { levels, columns } = getOrderOfLevels(levelNames ?? []);

  const checkHighlightedIndex = (levelIndex) => {
    return (
      syllabusIndex === firstIncompleteLevel.syllabusIndex &&
      moduleIndex === firstIncompleteLevel.moduleIndex &&
      levelIndex === firstIncompleteLevel.levelIndex
    );
  };

  const getIconOfSyllabus = (lesson) => {
    const modules = lesson.modules ?? [];
    const isModuleCompleted = (module) => {
      if (language === 'any') return module.exercise.isCompletedInAnyLanguage;
      const levelsCompleted = module.exercise.languagesCompleted[language] ?? [];
      return levelsCompleted.every((level) => level.isCompleted);
    };

    const isLessonCompleted = modules.every(isModuleCompleted);
    const hasPaidExercise = modules.some((module) => module.accessLevel === 'paid');

    let icon, color;

    if (isLessonCompleted) {
      icon = 'fa-solid fa-circle-check mr-2 fa-lg';
      color = colors.green4;
    }

    if (course.isPaidCourse && hasPaidExercise) {
      icon = 'fa fa-lock fa-sm mr-2';
    }

    if (isCurrentModule) {
      icon = 'fa-solid fa-circle-arrow-right mr-2 fa-lg';
      color = colors.lightBlue16;
    }

    return [icon, color];
  };

  const getStatusOfLevel = (isLevelCompleted, levelIndex) => {
    const { isPaidCourse } = course;
    const { _id: userId } = user;

    if (isLevelCompleted) return courseExerciseStatus.COMPLETED;

    if (checkHighlightedIndex(levelIndex)) return courseExerciseStatus.CURRENT;

    const isFreeAccess = (isPaidCourse && exerciseAccessLevel === 'free') || !isPaidCourse;
    if (isFreeAccess) return courseExerciseStatus.UNLOCKED;

    const isSignedInAndUnlocked = exerciseAccessLevel === 'signedIn' && userId;
    if (isSignedInAndUnlocked) return courseExerciseStatus.UNLOCKED;

    return courseExerciseStatus.LOCKED;
  };

  return (
    <FlowContainer id={`module-container-module-${syllabusIndex}-${moduleIndex}`}>
      <FlowHeadingContainer>
        <EmptyDiv>
          <i
            className={exerciseAccessLevel === 'signedIn' && user._id ? '' : getIconOfSyllabus(lesson)[0]}
            style={{ color: getIconOfSyllabus(lesson)[1] }}
          />
          {moduleName}
        </EmptyDiv>
        {isCurrentModule && (
          <PracticeButton
            id="practice-button"
            onClick={(event) =>
              handleClickOnLevelName(
                event,
                exerciseId,
                firstIncompleteLevel.levelIndex,
                testImpl,
                inputExercise,
                exerciseAccessLevel
              )
            }
          >
            {isFirstLevel ? 'Start' : 'Continue'}
          </PracticeButton>
        )}
      </FlowHeadingContainer>
      <LevelsContainer id={`module-${syllabusIndex}-${moduleIndex}`} height={getHeightOfContainer(levels.length)}>
        {columns.map((column, colIndex) => (
          <LevelColumn key={colIndex}>
            {column.map((item) => (
              <LevelItem
                key={item.index}
                item={item}
                course={course}
                language={language}
                isCompleted={isCompleted}
                languagesCompleted={languagesCompleted}
                syllabusIndex={syllabusIndex}
                moduleIndex={moduleIndex}
                lesson={lesson}
                moduleName={moduleName}
                levels={levels}
                lastCompletedLevel={lastCompletedLevel}
                handleClickOnLevelName={handleClickOnLevelName}
                isPaidExercise={isPaidExercise}
                checkHighlightedIndex={checkHighlightedIndex}
                getStatusOfLevel={getStatusOfLevel}
                exerciseId={exerciseId}
                testImpl={testImpl}
                inputExercise={inputExercise}
                isCurrentModule={isCurrentModule}
                isMobile={isMobile}
                exerciseAccessLevel={exerciseAccessLevel}
              />
            ))}
          </LevelColumn>
        ))}
      </LevelsContainer>

      <IconImage
        id={`image-${syllabusIndex}-${moduleIndex}`}
        src={courseIcons(`./${icon.name ?? 'default'}.png`)}
        style={getCharacterImages(levels.length, isMobile)}
        width={isMobile ? icon.width - 20 + 'px' : icon.width + 'px'}
        onLoad={() => setIsImageLoaded(true)}
      ></IconImage>
      {isImageLoaded && (
        <BubbleText
          exerciseName={exerciseName}
          levels={levels}
          imageId={`image-${syllabusIndex}-${moduleIndex}`}
          isMobile={isMobile}
        />
      )}
    </FlowContainer>
  );
}
